import axios, { AxiosInstance, AxiosError } from "axios";

import * as Config from "@app/configs";

class HttpService {
  http: AxiosInstance;
  authToken: string | null = null;

  constructor() {
    this.http = axios.create({
      baseURL: Config.BASE_URL + Config.API_PREFIX,
      timeout: Number(Config.API_TIMEOUT),
    });

    this.interceptRequests();
    this.interceptResponse();
  }

  interceptRequests() {
    /**
     * axios request interceptors for debugging
     * and altering request data
     */
    this.http.interceptors.request.use(
      async (reqConfig) => {
        const requestUrl = reqConfig.url ?? "";
        reqConfig.baseURL = Config.BASE_URL + Config.API_PREFIX;
        // ✅ Add Authorization header for other requests
        if (this.authToken) {
          reqConfig.headers.Authorization = `Bearer ${this.authToken}`;
        }
        console.log(`[BASE_URL] ${reqConfig.baseURL}`);
        console.log(`[Http.Request: ${requestUrl}]`, reqConfig);
        return reqConfig;
      },
      (error) => Promise.reject(error)
    );
  }

  interceptResponse() {
    /**
     * Customize axios success and error
     * data to easily handle them in the app
     */
    this.http.interceptors.response.use(
      (response) => {
        console.log(`[Http.Response: ${response.config.url ?? ""}]`, response);
        return response.data;
      },
      (error: AxiosError) => Promise.reject(this.handleApiError(error))
    );
  }

  // Handling error
  handleApiError(error: any) {
    try {
      if (error.response) {
        /*
         Able to connect with the server, but something
         went wrong and the API returned a reason for that
       */
        if (error.response.data?.StatusCode === 500) {
          return {
            message: "Server Error",
          };
        }
        return {
          message: error.response.data?.message ?? "Unknown error occurred",
        };
      }

      // Not able to connect with the server
      return { message: error.message };
    } catch {
      // Can't figure out the source of the error
      return { message: "Unknown error occurred" };
    }
  }

  setToken(token: string | null) {
    this.authToken = token;
  }

  getToken() {
    return this.authToken;
  }
}

const httpService = new HttpService();
const request = httpService.http;

export { httpService, request };
